import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ROUTER_NAVIGATION, RouterNavigationAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, filter, map, of, switchMap, take, tap } from 'rxjs';

import { CancellingReason } from '@ninety/platform/models/billing/cancelling-reason';
import { DowngradingFeaturesLost } from '@ninety/platform/models/billing/downgrading-features-lost';
import { PlanSubscriptionDetails } from '@ninety/platform/models/billing/plan-details';
import { PlanLevel } from '@ninety/platform/models/billing/plan-level';
import { PlanRank } from '@ninety/platform/models/billing/plan-rank';
import { PricingPlan } from '@ninety/platform/models/billing/pricing-plan';
import { StripeReason } from '@ninety/platform/models/billing/stripe-query-params';
import { SubscriptionV3 } from '@ninety/platform/models/billing/subscription';
import { FREQUENCY_DISPLAY_NAME } from '@ninety/platform/models/billing/subscription-frequencies.enum';
import { SubscriptionProvider } from '@ninety/platform/models/billing/subscription-provider.enum';
import { BillingV3Service } from '@ninety/platform/services/services/billing-v3.service';

import { selectAllCompanyUsers } from '../../app-entities/company-users/company-users-state.selectors';
import { UsersStateActions } from '../../app-entities/users/users-state.actions';
import { selectCurrentUserId, selectCurrentUserIsAdminOrOwner } from '../../app-entities/users/users-state.selectors';
import { appActions } from '../../app.actions';
import {
  CHECKOUT_DIALOG,
  CONFIRM_DIALOG,
  CONFIRM_LICENSE_CHANGE_DIALOG,
  DOWNGRADE_CONFIRMATION_DIALOG,
  DOWNGRADE_RETENTION_DIALOG,
  DOWNGRADE_RETENTION_STEP2_DIALOG,
  PRICING_TIER_DIALOG,
} from '../../tokens/billing-dialog.tokens';
import { CompanyActions } from '../company/company-state.actions';
import { selectCompany } from '../company/company-state.selectors';
import { CompanyService } from '../company/company.service';
import { SubscriptionActions } from '../company/subscription/subscription-state.actions';
import {
  selectDaysLeftInBillingPeriod,
  selectIsInactiveV3Company,
  selectSubscription,
  selectSubscriptionConfigId,
} from '../company/subscription/subscription-state.selectors';
import { NotificationActions } from '../notifications/notification.actions';
import { SpinnerActions } from '../spinner/spinner-state.actions';

import { BillingStateActions } from './billing-state.actions';
import {
  selectCurrentSubscriptionConfig,
  selectFutureSubscriptionConfigId,
  selectIsFreeOrTrialing,
  selectIsFuturePaidPlanAndIsTrialing,
  selectIsStripeFreemium,
  selectIsTrialing,
  selectPricingPlans,
  selectStripeQueryParams,
  selectUseSubscriptionScheduleFlow,
} from './billing-state.selectors';

/**
 * Stable `MatDialog` ids for the pricing-tier, checkout and downgrade-retention step-2 dialogs. The
 * effects inspect open dialogs by id (`dialog.getDialogById`) instead of
 * `componentInstance instanceof XComponent`, so they never hold a reference to a `shared/ui/core`
 * component class (DEV-26105 T6b — that `instanceof` reference was a
 * `platform/state -> shared/ui/core` edge token-inversion alone could not sever).
 */
const PRICING_TIER_DIALOG_ID = 'billing-pricing-tier-dialog';
const CHECKOUT_DIALOG_ID = 'billing-checkout-dialog';
const RETENTION_STEP2_DIALOG_ID = 'billing-downgrade-retention-step-2-dialog';

@Injectable()
export class BillingStateEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly billingV3Service = inject(BillingV3Service);
  private readonly router = inject(Router);
  private readonly companyService = inject(CompanyService);

  private readonly openCheckout = inject(CHECKOUT_DIALOG);
  private readonly openPricingTier = inject(PRICING_TIER_DIALOG);
  private readonly openConfirmLicenseChange = inject(CONFIRM_LICENSE_CHANGE_DIALOG);
  private readonly openDowngradeConfirmation = inject(DOWNGRADE_CONFIRMATION_DIALOG);
  private readonly openDowngradeRetention = inject(DOWNGRADE_RETENTION_DIALOG);
  private readonly openDowngradeRetentionStep2 = inject(DOWNGRADE_RETENTION_STEP2_DIALOG);
  private readonly openConfirmDialog = inject(CONFIRM_DIALOG);

  // NOTE: Handles confirming subscription upgrade no matter where the user is in the app when they choose to upgrade
  // NOTE: 'rna' - return no action | 'cs' - checkout session
  // check 'plan' param to know we are on the new subscription schedule flow
  confirmSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      map((action: RouterNavigationAction) => action.payload.routerState.root.queryParams),
      filter(({ plan, reason }) => !!plan && reason === 'cs'),
      map(({ plan }) => BillingStateActions.openCheckoutModal({ selectedPlanId: plan }))
    )
  );

  /** on app load, check to see if we were redirected from stripe and if so, handle accordingly */
  handleStripeRedirectOnInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.dispatchAppStartActions),
      switchMap(() =>
        this.store.select(selectCompany).pipe(
          filter(company => !!company),
          take(1)
        )
      ),
      concatLatestFrom(() => this.store.select(selectStripeQueryParams)),
      filter(([, queryParams]) => !!queryParams),
      map(([, { configId, reason }]) => {
        /** the plan was changed, check if downgrading and if so, save the future config id */
        if (configId) return SubscriptionActions.getUpdatedSubscriptionAfterPlanChange({ configId });

        /** typically because we are adding users or changing to paid roles */
        if (reason === StripeReason.SubscriptionUpdateConfirmation)
          return BillingStateActions.getBillingCountsAfterStripeRedirect();

        /** handle cancellation */
        //  if (reason === StripeReason.SubscriptionCancel)

        /** always clear the stripe query params, even if no action is taken on return */
        return BillingStateActions.noActionNeededAfterStripeRedirect();
      })
    )
  );

  /** If this is an inactive company, we force the admin or owners to select a plan by opening the
   * pricing plan modal and don't let them close it til they select a plan */
  checkIfInactiveBillingV3Company$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.dispatchAppStartActions),
      switchMap(() =>
        this.store.select(selectCompany).pipe(
          filter(company => !!company),
          take(1)
        )
      ),
      concatLatestFrom(() => [
        this.store.select(selectIsInactiveV3Company),
        this.store.select(selectCurrentUserIsAdminOrOwner),
        this.store.select(selectStripeQueryParams),
      ]),
      filter(([, isInactive, isAdminOrOwner, queryParams]) => isInactive && isAdminOrOwner && !queryParams),
      map(() => BillingStateActions.openPricingPlansModal())
    )
  );

  /** if there are no stripe query params, handle getting the updated subscription,
   * if there are, this is handled elsewhere  */
  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.init, BillingStateActions.noActionNeededAfterStripeRedirect),
      switchMap(() =>
        this.store.select(selectCompany).pipe(
          filter(company => !!company),
          take(1)
        )
      ),
      concatLatestFrom(() => this.store.select(selectStripeQueryParams)),
      filter(([, queryParams]) => !queryParams),
      map(() => SubscriptionActions.getUpdatedSubscription())
    )
  );

  getUpdatedSubscriptionAfterPlanChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionActions.getUpdatedSubscriptionAfterPlanChange),
      concatLatestFrom(() => this.store.select(selectCompany)),
      filter(([, company]) => !!company),
      switchMap(([{ configId }]) =>
        this.billingV3Service.getUpdatedSubscription().pipe(
          map(subscription =>
            SubscriptionActions.getUpdatedSubscriptionAfterPlanChangeSuccess({ subscription, configId })
          ),
          catchError((error: unknown) =>
            of(NotificationActions.notifyError({ error, message: 'Failed to get updated subscription' }))
          )
        )
      )
    )
  );

  /** only matters when downgrading, but also need to update when upgrading to so we won't show incorrect info on billing page */
  saveFutureState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionActions.getUpdatedSubscriptionAfterPlanChangeSuccess),
      map(({ configId }) => BillingStateActions.saveFutureSubscriptionConfigId({ configId }))
    )
  );

  saveFutureConfigIdAfterDowngrade$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.saveFutureSubscriptionConfigId),
      switchMap(({ configId }) => this.billingV3Service.saveFutureSubscriptionConfig(configId)),
      map(subscription => SubscriptionActions.saveFutureSubscriptionConfigSuccess({ subscription }))
    )
  );

  /** the reason these are separated is we show a confirmation modal ONLY after a successful downgrade
   * and not on app load/regular visit to the billing page where we still need to fetch the future subscription config */
  getFutureSubscriptionConfigAfterDowngrade$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.saveFutureSubscriptionConfigId),
      concatLatestFrom(() => this.store.select(selectSubscriptionConfigId)),
      filter(([{ configId }, currentConfigId]) => configId != currentConfigId),
      switchMap(([{ configId }]) => this.billingV3Service.getSubscriptionConfigById(configId)),
      map(futureSubscriptionConfig =>
        BillingStateActions.getFutureSubscriptionConfigAfterDowngradeSuccess({ futureSubscriptionConfig })
      )
    )
  );

  getFutureSubscriptionConfigOnAppLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.getFutureSubscriptionConfig),
      switchMap(({ configId }) => this.billingV3Service.getSubscriptionConfigById(configId)),
      map(futureSubscriptionConfig =>
        BillingStateActions.getFutureSubscriptionConfigSuccess({ futureSubscriptionConfig })
      )
    )
  );

  getBillingCountsAfterStripeRedirect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.getBillingCountsAfterStripeRedirect),
      switchMap(() => this.billingV3Service.getBillingCounts()),
      map(companyBillingCounts =>
        BillingStateActions.getBillingCountsAfterStripeRedirectSuccess({ companyBillingCounts })
      )
    )
  );

  getUpdatedSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SubscriptionActions.getUpdatedSubscription,
        BillingStateActions.getBillingCountsAfterStripeRedirect,
        BillingStateActions.noActionNeededAfterStripeRedirect,
        BillingStateActions.convertInactiveToFreemiumSuccess,
        BillingStateActions.updateNextPhaseSuccess
      ),
      // take(1) is load-bearing: this effect's own success rewrites the company, so without it the
      // still-subscribed select re-emits and re-fires the fetch on every response.
      switchMap(() =>
        this.store.select(selectCompany).pipe(
          filter(company => !!company),
          take(1)
        )
      ),
      exhaustMap(() =>
        this.billingV3Service.getUpdatedSubscription().pipe(
          map(subscription => SubscriptionActions.getUpdatedSubscriptionSuccess({ subscription })),
          catchError((error: unknown) =>
            of(
              SubscriptionActions.getUpdatedSubscriptionFailure({
                error,
                customMessage: 'Failed to get updated subscription',
              })
            )
          )
        )
      )
    )
  );

  checkIfNeedToFetchFutureSubscriptionConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionActions.getUpdatedSubscriptionSuccess),
      concatLatestFrom(() => [this.store.select(selectUseSubscriptionScheduleFlow)]),
      filter(
        ([{ subscription }, useSubscriptionScheduleFlow]) =>
          (subscription.selectedFutureConfigurationId?.length &&
            subscription.selectedFutureConfigurationId !== subscription.subscriptionConfigurationId) ||
          useSubscriptionScheduleFlow
      ),
      map(([{ subscription }]) =>
        BillingStateActions.getFutureSubscriptionConfig({
          configId: subscription.selectedFutureConfigurationId,
        })
      )
    )
  );

  handleGetUpdatedSubscriptionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SubscriptionActions.getUpdatedSubscriptionSuccess,
        SubscriptionActions.getUpdatedSubscriptionAfterPlanChangeSuccess,
        SubscriptionActions.saveFutureSubscriptionConfigSuccess
      ),
      map(action => CompanyActions.updateCompany({ changes: { subscription: action.subscription } }))
    )
  );

  getCurrentPlan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SubscriptionActions.getUpdatedSubscriptionSuccess,
        SubscriptionActions.getUpdatedSubscriptionAfterPlanChangeSuccess
      ),
      switchMap(({ subscription }) =>
        this.billingV3Service.getSubscriptionConfigById(subscription.subscriptionConfigurationId)
      ),
      map(currentSubscriptionConfig =>
        BillingStateActions.getCurrentSubscriptionConfigSuccess({ currentSubscriptionConfig })
      )
    )
  );

  getCommercialModelSubscriptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.init, BillingStateActions.getCommercialModelSubscriptions),
      concatLatestFrom(() => [
        this.store.select(selectIsTrialing),
        this.store.select(selectSubscriptionConfigId),
        this.store.select(selectFutureSubscriptionConfigId),
        this.store.select(selectSubscription),
        this.store.select(selectIsFuturePaidPlanAndIsTrialing),
        this.store.select(selectUseSubscriptionScheduleFlow),
      ]),
      exhaustMap(
        ([
          _,
          isTrialing,
          configId,
          futureConfigId,
          currentSubscription,
          isFuturePaidPlanAndIsTrialing,
          useSubscriptionScheduleFlow,
        ]) =>
          this.billingV3Service.getCommercialModelSubscriptions().pipe(
            map(subscriptions => {
              const currentSub = subscriptions.find(s => s.id === configId);
              const recommendedTier = PlanLevel.Thrive;

              /** first map is temporary - making it easy to delete. but this is to ensure both localhost and dev work */
              const pricingPlans: PricingPlan[] = subscriptions
                .map(sub => ({
                  ...sub,
                  planLevel: sub.planLevel ?? PlanLevel[sub.displayName] ?? PlanLevel.Freemium,
                  displayName: sub.displayName === 'Freemium' ? 'Free' : sub.displayName,
                }))
                .map(sub => ({
                  ...sub,
                  details: PlanSubscriptionDetails[sub.planLevel],
                  isCurrentPlan: sub.id === currentSub?.id && !useSubscriptionScheduleFlow,
                  futurePlanStartDate:
                    sub.id === futureConfigId ? new Date(currentSubscription.currentPeriodEnd) : undefined,
                  isRecommended: sub.planLevel === recommendedTier,
                  isCurrentTrial: isTrialing && sub.planLevel === PlanLevel.Thrive,
                  isFreeAndTrialing: isTrialing && sub.planLevel === PlanLevel.Freemium,
                  price: sub?.price ? parseInt(sub.price as any, 10) : 0,
                  isFuturePaidPlanAndIsTrialing: isFuturePaidPlanAndIsTrialing,
                  honoringTrial:
                    isFuturePaidPlanAndIsTrialing && currentSubscription.subscriptionConfigurationId === sub.id,
                  futureSubscriptionConfigurationId: currentSubscription?.selectedFutureConfigurationId ?? '',
                  useSubscriptionScheduleFlow: useSubscriptionScheduleFlow,
                }))
                /** sort by price because they are not guaranteed to come back in the correct order from the db */
                .sort((a, b) => a.price - b.price);

              return BillingStateActions.getCommercialModelSubscriptionsSuccess({ pricingPlans });
            })
          )
      )
    )
  );

  selectPlan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.selectPlan),
      concatLatestFrom(() => [
        this.store.select(selectIsFreeOrTrialing),
        this.store.select(selectCurrentSubscriptionConfig),
        this.store.select(selectPricingPlans),
        this.store.select(selectSubscription),
        this.store.select(selectDaysLeftInBillingPeriod),
        this.store.select(selectIsInactiveV3Company),
      ]),

      map(([{ plan }, isFreeOrTrialing, currentSubscriptionConfig, plans, subscription, daysLeft, isInactive]) => {
        const selectedPlan = plans.find(p => p.id === plan.id);

        if (isInactive && selectedPlan.provider === SubscriptionProvider.Internal) {
          return BillingStateActions.convertInactiveToFreemium();
        }

        /** upgrading, choosing the same plan but different frequency, or selecting any plan when trialing */
        if (
          isFreeOrTrialing ||
          isInactive ||
          PlanRank[selectedPlan.planLevel] >= PlanRank[currentSubscriptionConfig.planLevel]
        ) {
          return BillingStateActions.getCheckoutUrl({ plan });
        }
        /** downgrading */
        return BillingStateActions.openRetentionDialog({
          data: {
            selectedPlan,
            subscription: subscription as SubscriptionV3,
            featuresLost:
              DowngradingFeaturesLost[`${currentSubscriptionConfig.planLevel}_To_${selectedPlan.planLevel}`],
            cancelReasons: Object.values(CancellingReason),
            daysLeftInBillingPeriod: daysLeft,
            pricingPlans: plans,
            currentPlanName: currentSubscriptionConfig.displayName,
            toFree: selectedPlan.planLevel === PlanLevel.Freemium,
          },
        });
      })
    )
  );

  /** when selecting free plan or canceling */
  selectFreePlan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.selectFreemiumPlan),
      concatLatestFrom(() => this.store.select(selectPricingPlans)),
      map(([, plans]) => {
        const plan = plans.find(p => p.planLevel === PlanLevel.Freemium);
        return BillingStateActions.selectPlan({ plan });
      })
    )
  );

  convertInactiveToFreemium$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.convertInactiveToFreemium),
      exhaustMap(() =>
        this.billingV3Service.convertInactiveToFreemium().pipe(
          map(() => BillingStateActions.convertInactiveToFreemiumSuccess()),
          catchError((error: unknown) =>
            of(
              NotificationActions.notifyError({
                error,
                message: 'Something went wrong.  Could not convert to Free plan.',
              })
            )
          )
        )
      )
    )
  );

  convertInactiveToFreemiumSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BillingStateActions.convertInactiveToFreemiumSuccess),
        tap(() => {
          this.dialog.getDialogById(PRICING_TIER_DIALOG_ID)?.close();
        })
      ),
    { dispatch: false }
  );

  updateNextPhaseSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BillingStateActions.updateNextPhaseSuccess),
        tap(() => {
          this.dialog.getDialogById(PRICING_TIER_DIALOG_ID)?.close();
          this.dialog.getDialogById(CHECKOUT_DIALOG_ID)?.close();
        })
      ),
    { dispatch: false }
  );

  openDowngradeRetentionModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BillingStateActions.openRetentionDialog),
        tap(({ data }) => {
          this.openDowngradeRetention({
            width: '662px',
            data,
          });
        })
      ),
    { dispatch: false }
  );

  openDowngradeRetentionStep2Modal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BillingStateActions.openRetentionStep2Dialog),
        tap(({ data }) => {
          if (this.dialog.getDialogById(RETENTION_STEP2_DIALOG_ID)) return;

          this.openDowngradeRetentionStep2({
            id: RETENTION_STEP2_DIALOG_ID,
            width: '500px',
            data,
          });
        })
      ),
    { dispatch: false }
  );

  recordCancellationReason$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.recordCancellationReason),
      switchMap(({ reasons, plan }) =>
        this.companyService.update({ cancelReasons: reasons }).pipe(
          map(() => BillingStateActions.downgradeConfirmed({ plan })),
          catchError((error: unknown) =>
            of(
              NotificationActions.notifyError({
                error,
                message: 'Could not save cancellation reason, but your cancellation will proceed.',
              }),
              BillingStateActions.downgradeConfirmed({ plan })
            )
          )
        )
      )
    )
  );

  downgradeConfirmed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.downgradeConfirmed),
      map(({ plan }) => {
        if (plan.provider === SubscriptionProvider.Internal) return BillingStateActions.getCancelUrl();
        return BillingStateActions.getCheckoutUrl({ plan });
      })
    )
  );

  openDowngradeConfirmationDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BillingStateActions.getFutureSubscriptionConfigAfterDowngradeSuccess),
        tap(({ futureSubscriptionConfig: { displayName, provider, frequency } }) => {
          this.openDowngradeConfirmation({
            width: '560px',
            data: {
              toFree: provider === SubscriptionProvider.Internal,
              displayName,
              frequency: FREQUENCY_DISPLAY_NAME[frequency],
            },
          });
        })
      ),
    { dispatch: false }
  );

  getStripePlanCheckoutUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.getCheckoutUrl),
      filter(({ plan }) => plan.provider === SubscriptionProvider.Stripe),
      concatLatestFrom(() => [
        this.store.select(selectIsFreeOrTrialing),
        this.store.select(selectIsInactiveV3Company),
        this.store.select(selectIsStripeFreemium),
      ]),
      switchMap(([{ plan }, isFreeOrTrialing, isInactive, isStripeFreemium]) =>
        this.billingV3Service
          .getCheckoutUrl(isInactive || (isFreeOrTrialing && !isStripeFreemium), {
            subscriptionConfigurationId: plan.id,
            redirectUrl: window.location.href,
          })
          .pipe(
            map(({ redirectUrl }) => BillingStateActions.routeToStripePortal({ redirectUrl })),
            catchError((error: unknown) => {
              this.dialog.getDialogById(RETENTION_STEP2_DIALOG_ID)?.close();
              return of(NotificationActions.notifyError({ error, message: 'Failed to get checkout url from Stripe' }));
            })
          )
      )
    )
  );

  getCancelUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.getCancelUrl),
      switchMap(() =>
        this.billingV3Service.getCancelUrl().pipe(
          map(({ redirectUrl }) => BillingStateActions.routeToStripePortal({ redirectUrl })),
          catchError((error: unknown) => {
            this.dialog.getDialogById(RETENTION_STEP2_DIALOG_ID)?.close();
            return of(NotificationActions.notifyError({ error, message: 'Failed to get cancel url from Stripe' }));
          })
        )
      )
    )
  );

  /** renew a subscription for a company with a planned cancellation */
  renewSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.renewSubscription),
      switchMap(() =>
        this.billingV3Service.resumeSubscription().pipe(
          map(subscription => BillingStateActions.renewSubscriptionSuccess({ subscription })),
          catchError((error: unknown) =>
            of(NotificationActions.notifyError({ error, message: 'Failed to renew subscription' }))
          )
        )
      )
    )
  );

  updateSubscriptionStateAfterRenewal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.renewSubscriptionSuccess),
      map(SubscriptionActions.getUpdatedSubscriptionSuccess)
    )
  );

  cancelFutureDowngrade$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.cancelFutureDowngrade),
      switchMap(() =>
        this.openConfirmDialog({
          data: {
            title: 'Keep your current plan?',
            message: "You're about to cancel your changes and keep your current plan.",
            confirmButtonText: 'Keep current plan',
            cancelButtonText: 'Go back',
          },
        }).afterClosed()
      ),
      filter(keepCurrentPlan => !!keepCurrentPlan),
      map(() => BillingStateActions.renewSubscription())
    )
  );

  routeToStripePortal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BillingStateActions.routeToStripePortal),
        tap(({ redirectUrl }) => {
          window.location.href = redirectUrl;
        })
      ),
    { dispatch: false }
  );

  getUpcomingInvoiceOnBillingInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.init),
      switchMap(() => this.store.select(selectCurrentUserIsAdminOrOwner)),
      filter(isAdminOrOwner => isAdminOrOwner),
      map(() => BillingStateActions.getUpcomingInvoice())
    )
  );

  getCompanyBillingCounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.init, BillingStateActions.getCompanyBillingCounts),
      exhaustMap(() =>
        this.billingV3Service.getBillingCounts().pipe(
          map(response => {
            if (!response) {
              return BillingStateActions.getCompanyBillingCountsFailure({
                error: null,
                customMessage: 'Failed to get company billing counts',
              });
            }
            return BillingStateActions.getCompanyBillingCountsSuccess({
              companyBillingCounts: response,
            });
          }),
          catchError((error: unknown) =>
            of(
              BillingStateActions.getCompanyBillingCountsFailure({
                error,
                customMessage: 'Failed to get company billing counts',
              })
            )
          )
        )
      )
    )
  );

  getBillingCountsAfterChangingNumberOfPaidUsersV3$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.getCurrentSubscriptionConfigSuccess),
      map(() => BillingStateActions.getCompanyBillingCounts())
    )
  );

  changeLicenseCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.changeLicenseCount),
      concatLatestFrom(() => [this.store.select(selectCurrentUserIsAdminOrOwner)]),
      filter(([_, isAdminOrOwner]) => isAdminOrOwner),
      switchMap(([{ quantity }]) =>
        this.billingV3Service.getChangeQuantityUrl(quantity, window.location.href).pipe(
          map(({ redirectUrl }) => BillingStateActions.routeToStripePortal({ redirectUrl })),
          catchError((error: unknown) =>
            of(NotificationActions.notifyError({ error, message: 'Failed to redirect to Stripe' }))
          )
        )
      )
    )
  );

  getUpcomingInvoice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.getUpcomingInvoice),
      exhaustMap(() =>
        this.billingV3Service.getUpcomingInvoice().pipe(
          map(response => {
            if (!response) {
              return BillingStateActions.getUpcomingInvoiceFailure({
                error: null,
                customMessage: 'Failed to get upcoming invoice',
              });
            }
            return BillingStateActions.getUpcomingInvoiceSuccess({ invoice: response });
          }),
          catchError((error: unknown) =>
            of(
              BillingStateActions.getUpcomingInvoiceFailure({
                error,
                customMessage: 'Failed to get upcoming invoice',
              })
            )
          )
        )
      )
    )
  );

  startSpinner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BillingStateActions.recordCancellationReason,
        BillingStateActions.downgradeConfirmed,
        BillingStateActions.getCancelUrl,
        BillingStateActions.getCheckoutUrl,
        BillingStateActions.renewSubscription,
        BillingStateActions.convertInactiveToFreemium,
        BillingStateActions.updateNextPhase
      ),
      map(action => SpinnerActions.startPrimary({ source: action.type }))
    )
  );

  stopSpinner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BillingStateActions.getCompanyBillingCountsSuccess,
        BillingStateActions.getCompanyBillingCountsFailure,
        NotificationActions.notifyError,
        BillingStateActions.renewSubscriptionSuccess,
        BillingStateActions.convertInactiveToFreemiumSuccess,
        BillingStateActions.updateNextPhaseSuccess,
        BillingStateActions.updateNextPhaseFailure
      ),
      map(action => SpinnerActions.stopPrimary({ source: action.type }))
    )
  );

  openPricingTierDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BillingStateActions.openPricingPlansModal),
        concatLatestFrom(() => [
          this.store.select(selectCurrentSubscriptionConfig),
          this.store.select(selectIsInactiveV3Company),
          this.store.select(selectAllCompanyUsers),
        ]),
        tap(([, currentPlan, inactiveAccount, companyUsers]) => {
          this.dialog.getDialogById(CHECKOUT_DIALOG_ID)?.close();

          if (this.dialog.getDialogById(PRICING_TIER_DIALOG_ID)) {
            return;
          }

          /** don't prevent close if the user is in more than one company */
          const isInactive = inactiveAccount && companyUsers.length === 1;

          this.openPricingTier({
            id: PRICING_TIER_DIALOG_ID,
            panelClass: 'pricing-tier-modal-container',
            data: { currentPlan, isInactive },
            /* on inactive companies, don't let user close until they select a plan */
            disableClose: isInactive,
            autoFocus: false,
          });
        })
      ),
    { dispatch: false }
  );

  openCheckoutModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BillingStateActions.openCheckoutModal),
        tap(({ selectedPlanId }) => {
          this.dialog.getDialogById(PRICING_TIER_DIALOG_ID)?.close();

          if (this.dialog.getDialogById(CHECKOUT_DIALOG_ID)) {
            return;
          }

          this.openCheckout({
            id: CHECKOUT_DIALOG_ID,
            panelClass: 'checkout-container',
            data: { subscriptionConfigurationId: selectedPlanId },
            disableClose: false,
            autoFocus: false,
          });
        })
      ),
    { dispatch: false }
  );

  // banner on the homepage "Your free trial of Thrive is over..."
  dismissFreePlanNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.dismissFreePlanNotification),
      concatLatestFrom(() => this.store.select(selectCurrentUserId)),
      map(([_, userId]) => UsersStateActions.update({ userId, update: { freePlanNotificationDismissed: true } }))
    )
  );

  dismissRenewalNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.dismissRenewalNotification),
      concatLatestFrom(() => this.store.select(selectCurrentUserId)),
      filter(([{ key }]) => !!key),
      map(([{ key }, userId]) => UsersStateActions.update({ userId, update: { renewalNotificationDismissedKey: key } }))
    )
  );

  private confirmLicenseChangeDialogRef: MatDialogRef<unknown, number>;
  openConfirmLicenseChangeDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BillingStateActions.openConfirmLicenseChangeDialog),
        tap(({ data }) => {
          this.confirmLicenseChangeDialogRef = this.openConfirmLicenseChange({
            width: '400px',
            data,
          });
        })
      ),
    { dispatch: false }
  );

  cancelLicenseChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BillingStateActions.cancelLicenseChange),
        tap(() => this.confirmLicenseChangeDialogRef.close())
      ),
    { dispatch: false }
  );

  clearStripeQueryParams$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          BillingStateActions.clearStripeQueryParams,
          SubscriptionActions.getUpdatedSubscriptionAfterPlanChange,
          BillingStateActions.noActionNeededAfterStripeRedirect
        ),
        tap(() => {
          this.router.navigate([], {
            queryParams: { external: null, reason: null, configId: null, plan: null },
            queryParamsHandling: 'merge',
          });
        })
      ),
    { dispatch: false }
  );

  getBillingPortalUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.getBillingPortalUrl),
      switchMap(() => this.billingV3Service.getBillingPortalUrl(window.location.href)),
      map(({ redirectUrl }) => BillingStateActions.routeToStripePortal({ redirectUrl }))
    )
  );

  getSetupUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.getSetupUrl),
      switchMap(({ redirectUrl }) => this.billingV3Service.getSetupUrl(redirectUrl)),
      map(({ redirectUrl }) => BillingStateActions.routeToStripePortal({ redirectUrl }))
    )
  );

  updateNextPhase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.updateNextPhase),
      switchMap(({ subscriptionConfigurationId, promoCodes }) =>
        this.billingV3Service.updateNextPhase(subscriptionConfigurationId, promoCodes).pipe(
          map(subscription => BillingStateActions.updateNextPhaseSuccess({ subscription })),
          catchError((error: unknown) =>
            of(
              BillingStateActions.updateNextPhaseFailure({
                error,
                customMessage: 'Failed to update next phase',
              })
            )
          )
        )
      )
    )
  );
}
